import { useId } from 'react';

/**
 * Native `<select>` для форм. Block 0 Phase 2 / Step 3.
 *
 * Принципиально нативный (не custom dropdown) — на мобильных платформах
 * нативный picker даёт лучший UX (скролл, поиск, accessibility системного
 * UI), и P-019 «вертикальные кнопки только» не дружит с горизонтальными
 * кастомными dropdown-стрелками.
 *
 * Контракт `options`: `Array<{ value, label }>`. `value` — это то, что
 * попадает в `onChange`; `label` — то, что видит пользователь.
 *
 * `placeholder` — псевдо-вариант с `value=""`. Если `placeholder` передан,
 * первый option рендерится с пустым value и `disabled`, чтобы пользователь
 * не мог «вернуться к плейсхолдеру» после выбора (стандартный native-паттерн).
 *
 * P-022 a11y baseline — htmlFor / aria-invalid / aria-describedby /
 * focus-visible / min-h-tap.
 */
export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  name: string;
  label: string;
  /** `value=""` означает «не выбрано» (placeholder). */
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<SelectOption>;
  placeholder?: string;
  error?: string;
  hint?: string;
  disabled?: boolean;
  required?: boolean;
}

export function Select({
  name,
  label,
  value,
  onChange,
  options,
  placeholder,
  error,
  hint,
  disabled,
  required,
}: SelectProps) {
  const reactId = useId();
  const selectId = `${name}-${reactId}`;
  const errorId = `${selectId}-error`;
  const hintId = `${selectId}-hint`;

  const describedBy = error ? errorId : hint ? hintId : undefined;
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col">
      <label htmlFor={selectId} className="text-ink font-semibold text-base mb-2">
        {label}
      </label>
      <select
        id={selectId}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        aria-invalid={hasError || undefined}
        aria-describedby={describedBy}
        className={
          'min-h-tap rounded-xl bg-card text-ink text-base px-3 py-3 ' +
          'border-[1.5px] ' +
          (hasError ? 'border-error ' : 'border-accent ') +
          'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
          'disabled:bg-icon-bg disabled:text-muted disabled:cursor-not-allowed'
        }
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {hasError ? (
        <p id={errorId} role="alert" className="text-error text-sm mt-1">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-muted text-sm mt-1">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
